import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface Toast {
  id: string;
  type: 'cart' | 'success' | 'error' | 'info' | 'favorite' | 'favorite_remove' | 'promo_save' | 'promo_remove';
  title: string;
  message?: string;
  image?: string;
  color?: string;
  price?: number;
  link?: string;
  linkLabel?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random()}`;
    setToasts(prev => [...prev.slice(-3), { ...toast, id }]);

    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

/* ── Toast Container ── */
const ToastContainer = ({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) => (
  <div className="fixed bottom-24 right-6 z-[80] flex flex-col gap-3 items-end pointer-events-none">
    {toasts.map((toast, index) => (
      <ToastItem
        key={toast.id}
        toast={toast}
        onDismiss={onDismiss}
        index={index}
        total={toasts.length}
      />
    ))}
  </div>
);

/* ── Single Toast ── */
const ToastItem = ({
  toast,
  onDismiss,
  index,
  total,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
  index: number;
  total: number;
}) => {
  const [exiting, setExiting] = useState(false);

  const handleDismiss = () => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 280);
  };

  const accentColor = toast.color ?? (
    toast.type === 'success' ? '#39ff14' :
    toast.type === 'error' ? '#ff2d78' :
    toast.type === 'cart' ? '#ff2d78' :
    toast.type === 'favorite' ? '#ff2d78' :
    toast.type === 'favorite_remove' ? 'rgba(255,255,255,0.3)' :
    toast.type === 'promo_save' ? '#f0ff00' :
    toast.type === 'promo_remove' ? 'rgba(255,255,255,0.3)' :
    '#00f5ff'
  );

  const iconMap: Record<Toast['type'], string> = {
    cart: 'ri-shopping-bag-line',
    success: 'ri-checkbox-circle-line',
    error: 'ri-close-circle-line',
    info: 'ri-information-line',
    favorite: 'ri-heart-fill',
    favorite_remove: 'ri-heart-line',
    promo_save: 'ri-bookmark-fill',
    promo_remove: 'ri-bookmark-line',
  };

  return (
    <div
      className="pointer-events-auto relative overflow-hidden rounded-2xl flex items-start gap-3 p-4 cursor-pointer"
      style={{
        width: '320px',
        background: 'rgba(8,8,14,0.97)',
        border: `1px solid ${accentColor}30`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${accentColor}10`,
        backdropFilter: 'blur(20px)',
        opacity: exiting ? 0 : 1,
        transform: exiting
          ? 'translateX(120%) scale(0.9)'
          : 'translateX(0) scale(1)',
        transition: exiting
          ? 'opacity 0.28s ease, transform 0.28s cubic-bezier(0.4,0,1,1)'
          : `opacity 0.4s cubic-bezier(0.22,1,0.36,1), transform 0.4s cubic-bezier(0.22,1,0.36,1)`,
        animationName: exiting ? 'none' : 'toastSlideIn',
        animationDuration: '0.4s',
        animationTimingFunction: 'cubic-bezier(0.22,1,0.36,1)',
        animationFillMode: 'both',
      }}
      onClick={handleDismiss}
    >
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{ background: `linear-gradient(90deg, ${accentColor}, ${accentColor}40, transparent)` }}
      />

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 h-0.5 rounded-full"
        style={{
          background: accentColor,
          boxShadow: `0 0 6px ${accentColor}`,
          animation: 'toastProgress 3.5s linear forwards',
        }}
      />

      {/* Icon or image */}
      {toast.image ? (
        <div
          className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
          style={{ background: `linear-gradient(135deg, #0a0a14, ${accentColor}12)`, border: `1px solid ${accentColor}25` }}
        >
          <img
            src={toast.image}
            alt={toast.title}
            className="w-10 h-10 object-contain"
            style={{ filter: `drop-shadow(0 2px 6px ${accentColor}50)` }}
          />
        </div>
      ) : (
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
          style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}30` }}
        >
          <i className={`${iconMap[toast.type]} text-lg`} style={{ color: accentColor }} />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="font-orbitron font-bold text-xs text-white leading-snug mb-0.5">{toast.title}</p>
        {toast.message && (
          <p className="font-mono text-[11px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {toast.message}
          </p>
        )}
        {toast.price !== undefined && (
          <p className="font-orbitron font-black text-sm mt-1" style={{ color: accentColor, textShadow: `0 0 8px ${accentColor}60` }}>
            {toast.price.toLocaleString()} ₽
          </p>
        )}
        {toast.link && toast.linkLabel && (
          <Link
            to={toast.link}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 mt-1.5 font-mono text-[11px] transition-opacity hover:opacity-80"
            style={{ color: accentColor }}
          >
            {toast.linkLabel}
            <i className="ri-arrow-right-line text-[10px]" />
          </Link>
        )}
      </div>

      {/* Close */}
      <button
        onClick={e => { e.stopPropagation(); handleDismiss(); }}
        className="w-5 h-5 flex items-center justify-center rounded-full shrink-0 cursor-pointer transition-colors mt-0.5"
        style={{ color: 'rgba(255,255,255,0.25)' }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.7)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.25)'; }}
      >
        <i className="ri-close-line text-xs" />
      </button>

      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateX(60px) scale(0.92); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes toastProgress {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
};
